import { useState, useEffect } from 'react';
import * as React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaArrowLeft, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt,
  FaBriefcase, FaLanguage, FaUtensils, FaIdCard, FaUserCheck,
  FaChevronDown, FaGlobe, FaUserEdit, FaPhoneAlt, FaUser, FaEdit,
  FaSave, FaTimes, FaIceCream, FaStar, FaRegStar, FaClipboardList, FaStickyNote, FaFileAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import RecruitingService from '../services/RecruitingService';
import InterviewService from '../services/InterviewService';
import StatusBadge from './StatusBadge';
import StatusTimeline from './StatusTimeline';
import PhoneInterviewForm from './PhoneInterviewForm';
import InternalNotesPanel from './InternalNotesPanel';
import type { Applicant, ApplicantStatus, ApplicantCreateData, PhoneInterview, InterviewRecommendation } from '../types';
import { HttpError } from '../../../core/services/HttpService';

const STATUS_TRANSITIONS: Record<ApplicantStatus, { value: ApplicantStatus; label: string }[]> = {
  NEW: [
    { value: 'CONFIRMATION_SENT', label: 'Bestätigung senden' },
    { value: 'REJECTED', label: 'Ablehnen' },
  ],
  CONFIRMATION_SENT: [
    { value: 'PHONE_INTERVIEW_SCHEDULED', label: 'Interview planen' },
    { value: 'REJECTED', label: 'Ablehnen' },
  ],
  PHONE_INTERVIEW_SCHEDULED: [
    { value: 'PHONE_INTERVIEW_DONE', label: 'Interview abschließen' },
    { value: 'REJECTED', label: 'Ablehnen' },
  ],
  PHONE_INTERVIEW_DONE: [
    { value: 'TRIAL_SCHEDULED', label: 'Probearbeiten planen' },
    { value: 'REJECTED', label: 'Ablehnen' },
  ],
  TRIAL_SCHEDULED: [
    { value: 'TRIAL_DONE', label: 'Probearbeiten abschließen' },
    { value: 'REJECTED', label: 'Ablehnen' },
  ],
  TRIAL_DONE: [
    { value: 'HIRED', label: 'Einstellen' },
    { value: 'REJECTED', label: 'Ablehnen' },
  ],
  HIRED: [],
  REJECTED: [],
};

const GERMAN_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Muttersprache'];

const GERMAN_LEVEL_LABELS: Record<string, string> = {
  A1: 'A1 – Anfänger', A2: 'A2 – Grundlegende Kenntnisse',
  B1: 'B1 – Fortgeschritten', B2: 'B2 – Selbständig',
  C1: 'C1 – Fachkundig', C2: 'C2 – Annähernd muttersprachlich',
  Muttersprache: 'Muttersprache',
};

function formatDate(iso?: string): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso?: string): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatEmploymentType(type?: string): string {
  const map: Record<string, string> = { MINIJOB: 'Minijob', WERKSTUDENT: 'Werkstudent', TEILZEIT: 'Teilzeit', VOLLZEIT: 'Vollzeit', KURZFRISTIG: 'Kurzfristig' };
  return type ? (map[type] || type) : '–';
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
  fontSize: 13, color: colors.textBody, background: colors.bgCard, outline: 'none', width: '100%',
};

const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };

export default function ApplicantDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const applicantId = id ? parseInt(id, 10) : 0;

  const [applicant, setApplicant] = useState<Applicant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'profil' | 'interviews' | 'notizen'>('profil');
  const [interviews, setInterviews] = useState<PhoneInterview[]>([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<ApplicantCreateData & { favorite_ice_cream?: string; rejection_reason?: string }>>({});

  useEffect(() => {
    const scrollContainer = document.querySelector('[data-testid="main-content-scrollable"]');
    if (scrollContainer) scrollContainer.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!applicantId) return;
    loadApplicant();
    loadInterviews();
  }, [applicantId]);

  const loadApplicant = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await RecruitingService.getApplicant(applicantId);
      setApplicant(data);
    } catch (e: any) {
      setError('Bewerber konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const loadInterviews = async () => {
    if (!applicantId) return;
    try {
      setLoadingInterviews(true);
      const data = await InterviewService.fetchInterviews(applicantId);
      setInterviews(data);
    } catch {
      // silent
    } finally {
      setLoadingInterviews(false);
    }
  };

  const startEditing = () => {
    if (!applicant) return;
    setForm({
      first_name: applicant.first_name, last_name: applicant.last_name,
      email: applicant.email, phone: applicant.phone || '',
      street: applicant.street || '', house_no: applicant.house_no || '',
      zip_code: applicant.zip_code || '', city: applicant.city || '',
      team_choice: applicant.team_choice, desired_employment_type: applicant.desired_employment_type,
      desired_start_date: applicant.desired_start_date || '',
      desired_work_area: applicant.desired_work_area || '',
      hours_can_work: applicant.hours_can_work ?? undefined,
      is_over_18: applicant.is_over_18 ?? undefined,
      native_language: applicant.native_language || '',
      german_level: applicant.german_level || '',
      english_level: applicant.english_level || '',
      has_gastro_experience: applicant.has_gastro_experience ?? undefined,
      gastro_experience_years: applicant.gastro_experience_years ?? undefined,
      has_red_card: applicant.has_red_card ?? undefined,
      is_already_employed: applicant.is_already_employed ?? undefined,
      current_status: applicant.current_status || '',
      cover_letter: applicant.cover_letter || '',
      motivation_text: applicant.motivation_text || '',
      favorite_ice_cream: applicant.favorite_ice_cream || '',
      birthday: applicant.birthday || '',
    });
    setEditing(true);
  };

  const cancelEditing = () => { setEditing(false); setForm({}); };

  const handleSave = async () => {
    if (!applicant) return;
    try {
      setSaving(true);
      const cleanedForm = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== '' && v !== undefined)
      );
      const updated = await RecruitingService.updateApplicant(applicant.id, cleanedForm);
      setApplicant(updated);
      setEditing(false);
      toast.success('Bewerber aktualisiert.');
    } catch (e: any) {
      const detail = e?.data || e?.response?.data || e?.message || '';
      console.error('[ApplicantDetail] save error:', e, detail);
      toast.error(`Speichern fehlgeschlagen: ${typeof detail === 'object' ? JSON.stringify(detail) : detail}`);
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (updates: Partial<typeof form>) => setForm(prev => ({ ...prev, ...updates }));

  const handleStatusChange = async (newStatus: ApplicantStatus) => {
    if (!applicant) return;
    try {
      setChangingStatus(true);
      setStatusMenuOpen(false);
      const updated = await RecruitingService.changeStatus(applicant.id, newStatus);
      setApplicant(updated);
      toast.success(`Status geändert: ${newStatus}`);
    } catch (e: any) {
      toast.error('Statusänderung fehlgeschlagen.');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleConvert = async () => {
    if (!applicant) return;
    try {
      setConverting(true);
      setShowConvertConfirm(false);
      const result = await RecruitingService.convertToEmployee(applicant.id);
      toast.success('Bewerber wurde als Mitarbeiter angelegt!');
      navigate(`/employees/${result.employee_id}`);
    } catch (e: any) {
      const httpErr = e as HttpError;
      if (httpErr?.status === 409) {
        toast.error('Bewerber wurde bereits in einen Mitarbeiter umgewandelt.');
      } else {
        toast.error('Konvertierung fehlgeschlagen.');
      }
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div style={ps.pageContainer}>
        <div style={{ textAlign: 'center', paddingTop: 56 }}>
          <p style={{ color: colors.textMuted, fontSize: 13 }}>Lade Bewerber...</p>
        </div>
      </div>
    );
  }

  if (error || !applicant) {
    return (
      <div style={ps.pageContainer}>
        <Link to="/employees/recruiting" style={ps.backButton}>
          <FaArrowLeft size={14} /> Zurück zur Bewerberliste
        </Link>
        <div style={{ color: colors.danger, fontSize: 13, marginTop: 24 }}>{error || 'Bewerber nicht gefunden.'}</div>
      </div>
    );
  }

  const transitions = STATUS_TRANSITIONS[applicant.status] || [];
  const canConvert = applicant.status === 'TRIAL_DONE' || applicant.status === 'HIRED';
  const alreadyConverted = applicant.employee != null;

  return (
    <div style={ps.pageContainer}>
      <div>
      <Link to="/employees/recruiting" className="hover-back" style={ps.backButton}>
        <FaArrowLeft size={14} />
        Zurück zur Bewerberliste
      </Link>

      {/* Header: 2-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {/* Left: Applicant info card */}
        <div style={{ ...ps.card, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 16, flex: 1 }}>
              <div style={{
                width: 88, height: 88, borderRadius: 44, flexShrink: 0,
                background: `linear-gradient(135deg, ${colors.bgMuted} 0%, ${colors.border} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaUser size={36} color={colors.textLight} />
              </div>

              <div style={{ flex: 1 }}>
                {editing ? (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input value={form.first_name || ''} onChange={e => updateForm({ first_name: e.target.value })}
                      placeholder="Vorname" style={{ ...inputStyle, fontSize: 18, fontWeight: 600, flex: 1 }} />
                    <input value={form.last_name || ''} onChange={e => updateForm({ last_name: e.target.value })}
                      placeholder="Nachname" style={{ ...inputStyle, fontSize: 18, fontWeight: 600, flex: 1 }} />
                  </div>
                ) : (
                  <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.navy, margin: '0 0 10px 0' }}>
                    {applicant.first_name} {applicant.last_name}
                  </h1>
                )}

                {editing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FaEnvelope color={colors.textLight} size={12} />
                        <input value={form.email || ''} onChange={e => updateForm({ email: e.target.value })}
                          placeholder="E-Mail" style={inputStyle} type="email" />
                      </div>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FaPhone color={colors.textLight} size={12} />
                        <input value={form.phone || ''} onChange={e => updateForm({ phone: e.target.value })}
                          placeholder="Telefon / Mobil" style={inputStyle} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
                      {applicant.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaEnvelope color={colors.textLight} size={13} />
                          <span style={{ fontSize: 14, color: colors.textBody }}>{applicant.email}</span>
                        </div>
                      )}
                      {applicant.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaPhone color={colors.textLight} size={13} />
                          <span style={{ fontSize: 14, color: colors.textBody }}>{applicant.phone}</span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 20, marginBottom: 10, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaCalendarAlt color={colors.textLight} size={13} />
                        <span style={{ fontSize: 13, color: colors.textMuted }}>
                          <strong style={{ color: colors.textBody }}>Beworben:</strong> {formatDateTime(applicant.applied_at)}
                        </span>
                      </div>
                      {applicant.birthday && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaCalendarAlt color={colors.textLight} size={13} />
                          <span style={{ fontSize: 13, color: colors.textMuted }}>
                            <strong style={{ color: colors.textBody }}>Geb.datum:</strong> {formatDate(applicant.birthday)}
                          </span>
                        </div>
                      )}
                      {applicant.desired_start_date && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaCalendarAlt color={colors.textLight} size={13} />
                          <span style={{ fontSize: 13, color: colors.textMuted }}>
                            <strong style={{ color: colors.textBody }}>Gewünschter Start:</strong> {formatDate(applicant.desired_start_date)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
                      {applicant.desired_employment_type && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaBriefcase color={colors.textLight} size={13} />
                          <span style={{ fontSize: 13, color: colors.textMuted }}>
                            <strong style={{ color: colors.textBody }}>Beschäftigungsart:</strong> {formatEmploymentType(applicant.desired_employment_type)}
                          </span>
                        </div>
                      )}
                      {applicant.team_choice && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FaBriefcase color={colors.textLight} size={13} />
                          <span style={{ fontSize: 13, color: colors.textMuted }}>
                            <strong style={{ color: colors.textBody }}>Team:</strong> {applicant.team_choice === 'SERVICE' ? 'Service' : 'Produktion'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {applicant.source === 'WEBSITE' ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                          background: colors.infoLight, color: colors.info, border: `1px solid ${colors.info}20`,
                        }}>
                          <FaGlobe size={9} /> Website
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                          background: colors.bgMuted, color: colors.textMuted, border: `1px solid ${colors.border}`,
                        }}>
                          <FaUserEdit size={9} /> Manuell
                        </span>
                      )}

                      {/* CV Upload / Download */}
                      {applicant.cv_file_url ? (
                        <>
                          <a
                            href={applicant.cv_file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                              background: 'rgba(82, 113, 165, 0.08)', color: colors.slate,
                              border: '1px solid rgba(82, 113, 165, 0.2)', textDecoration: 'none',
                            }}
                          >
                            <FaFileAlt size={9} /> CV anzeigen
                          </a>
                          <button
                            onClick={async () => {
                              if (!window.confirm('CV-Datei entfernen?')) return;
                              try {
                                const updated = await RecruitingService.deleteCv(applicant.id);
                                setApplicant(updated);
                                toast.success('CV entfernt.');
                              } catch { toast.error('CV konnte nicht entfernt werden.'); }
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 500,
                              background: 'transparent', color: colors.textMuted,
                              border: `1px solid ${colors.border}`, cursor: 'pointer',
                            }}
                          >
                            <FaTimes size={8} />
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            type="file"
                            id="cv-upload"
                            accept=".pdf,.doc,.docx"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) { toast.error('Datei zu groß (max. 5 MB).'); return; }
                              try {
                                const updated = await RecruitingService.uploadCv(applicant.id, file);
                                setApplicant(updated);
                                toast.success('CV hochgeladen.');
                              } catch { toast.error('CV-Upload fehlgeschlagen.'); }
                              e.target.value = '';
                            }}
                          />
                          <label
                            htmlFor="cv-upload"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                              background: colors.bgMuted, color: colors.textMuted,
                              border: `1px dashed ${colors.border}`, cursor: 'pointer',
                            }}
                          >
                            <FaFileAlt size={9} /> CV hochladen
                          </label>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Status badge + Edit button */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
              <StatusBadge status={applicant.status} />
              {!editing ? (
                <button onClick={startEditing} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', borderRadius: 8,
                  border: `1px solid ${colors.border}`, background: colors.bgCard,
                  cursor: 'pointer', fontSize: 11, fontWeight: 500, color: colors.textBody,
                }}>
                  <FaEdit style={{ fontSize: 10 }} /> Bearbeiten
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={cancelEditing} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 8,
                    border: `1px solid ${colors.border}`, background: colors.bgCard,
                    cursor: 'pointer', fontSize: 11, fontWeight: 500, color: colors.textBody,
                  }}>
                    <FaTimes style={{ fontSize: 9 }} /> Abbrechen
                  </button>
                  <button onClick={handleSave} disabled={saving} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 8, border: 'none',
                    background: `linear-gradient(135deg, ${colors.navy}, ${colors.magenta})`,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: 11, fontWeight: 600, color: 'white', opacity: saving ? 0.7 : 1,
                  }}>
                    <FaSave style={{ fontSize: 9 }} /> {saving ? 'Speichern...' : 'Speichern'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          {editing ? (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.borderLight}`, display: 'flex', gap: 8 }}>
              <div style={{ flex: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaMapMarkerAlt color={colors.textLight} size={12} />
                <input value={form.street || ''} onChange={e => updateForm({ street: e.target.value })}
                  placeholder="Straße" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <input value={form.house_no || ''} onChange={e => updateForm({ house_no: e.target.value })}
                  placeholder="Nr." style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <input value={form.zip_code || ''} onChange={e => updateForm({ zip_code: e.target.value })}
                  placeholder="PLZ" style={inputStyle} />
              </div>
              <div style={{ flex: 2 }}>
                <input value={form.city || ''} onChange={e => updateForm({ city: e.target.value })}
                  placeholder="Stadt" style={inputStyle} />
              </div>
            </div>
          ) : (applicant.street || applicant.city) ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.borderLight}` }}>
              <FaMapMarkerAlt color={colors.textLight} size={13} />
              <span style={{ fontSize: 13, color: colors.textBody }}>
                {[applicant.street, applicant.house_no].filter(Boolean).join(' ')}
                {(applicant.zip_code || applicant.city) ? ', ' : ''}
                {[applicant.zip_code, applicant.city].filter(Boolean).join(' ')}
              </span>
            </div>
          ) : null}
        </div>

        {/* Right: Status Timeline + Actions */}
        <div style={{ ...ps.card, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.navy }}>Bewerbungsverlauf</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {transitions.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setStatusMenuOpen(!statusMenuOpen)} disabled={changingStatus} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 8,
                    border: `1px solid ${colors.border}`, background: colors.bgCard,
                    cursor: changingStatus ? 'not-allowed' : 'pointer',
                    fontSize: 11, fontWeight: 500, color: colors.textBody, opacity: changingStatus ? 0.6 : 1,
                  }}>
                    Status ändern <FaChevronDown style={{ fontSize: 8 }} />
                  </button>
                  {statusMenuOpen && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setStatusMenuOpen(false)} />
                      <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 4,
                        background: colors.bgCard, borderRadius: 10, border: `1px solid ${colors.border}`,
                        boxShadow: shadows.medium, zIndex: 100, minWidth: 200, overflow: 'hidden',
                      }}>
                        {transitions.map(t => (
                          <button key={t.value} onClick={() => handleStatusChange(t.value)} style={{
                            display: 'block', width: '100%', padding: '10px 14px',
                            background: 'none', border: 'none', textAlign: 'left',
                            fontSize: 12, cursor: 'pointer',
                            color: t.value === 'REJECTED' ? colors.danger : colors.textBody,
                            fontWeight: t.value === 'REJECTED' ? 600 : 400,
                          }}
                            onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                          >{t.label}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {applicant.status !== 'HIRED' && applicant.status !== 'REJECTED' && (
                <button onClick={() => setShowInterviewForm(true)} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', borderRadius: 8,
                  border: `1px solid ${colors.border}`, background: colors.bgCard,
                  cursor: 'pointer', fontSize: 11, fontWeight: 500, color: colors.textBody,
                }}>
                  <FaPhoneAlt style={{ fontSize: 9 }} /> Interview
                </button>
              )}
            </div>
          </div>

          <StatusTimeline applicant={applicant} />

          {(canConvert || alreadyConverted) && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.borderLight}` }}>
              {canConvert && !alreadyConverted && (
                <button onClick={() => setShowConvertConfirm(true)} disabled={converting} style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center',
                  padding: '10px 16px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white',
                  cursor: converting ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: converting ? 0.6 : 1,
                }}>
                  <FaUserCheck style={{ fontSize: 12 }} /> Als Mitarbeiter anlegen
                </button>
              )}
              {alreadyConverted && (
                <button onClick={() => navigate(`/employees/${applicant.employee}`)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center',
                  padding: '10px 16px', borderRadius: 10,
                  border: `1px solid ${colors.success}`, background: colors.successLight,
                  color: '#166534', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}>
                  <FaUserCheck style={{ fontSize: 12 }} /> Mitarbeiter anzeigen
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {([
          { key: 'profil' as const, label: 'Profil', icon: <FaUser style={{ fontSize: 11 }} /> },
          { key: 'interviews' as const, label: `Interviews (${interviews.length})`, icon: <FaClipboardList style={{ fontSize: 11 }} /> },
          { key: 'notizen' as const, label: 'Notizen', icon: <FaStickyNote style={{ fontSize: 11 }} /> },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 9999, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
              border: activeTab === tab.key ? `1px solid rgba(82, 113, 165, 0.3)` : '1px solid transparent',
              background: activeTab === tab.key ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
              color: activeTab === tab.key ? colors.slate : colors.textMuted,
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Profil */}
      {activeTab === 'profil' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Kontakt & Adresse */}
        <div style={{ ...ps.card, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: colors.navy }}>Kontakt & Adresse</h3>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <EditField label="E-Mail" icon={<FaEnvelope />}>
                <input value={form.email || ''} onChange={e => updateForm({ email: e.target.value })} style={inputStyle} type="email" />
              </EditField>
              <EditField label="Telefon / Mobil" icon={<FaPhone />}>
                <input value={form.phone || ''} onChange={e => updateForm({ phone: e.target.value })} style={inputStyle} />
              </EditField>
              <EditField label="Straße" icon={<FaMapMarkerAlt />}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={form.street || ''} onChange={e => updateForm({ street: e.target.value })} placeholder="Straße" style={{ ...inputStyle, flex: 3 }} />
                  <input value={form.house_no || ''} onChange={e => updateForm({ house_no: e.target.value })} placeholder="Nr." style={{ ...inputStyle, flex: 1 }} />
                </div>
              </EditField>
              <EditField label="Ort" icon={<FaMapMarkerAlt />}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={form.zip_code || ''} onChange={e => updateForm({ zip_code: e.target.value })} placeholder="PLZ" style={{ ...inputStyle, flex: 1 }} />
                  <input value={form.city || ''} onChange={e => updateForm({ city: e.target.value })} placeholder="Stadt" style={{ ...inputStyle, flex: 2 }} />
                </div>
              </EditField>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <InfoRow icon={<FaEnvelope />} label="E-Mail" value={applicant.email} />
              <InfoRow icon={<FaPhone />} label="Telefon" value={applicant.phone} />
              <InfoRow icon={<FaMapMarkerAlt />} label="Adresse" value={
                [applicant.street, applicant.house_no].filter(Boolean).join(' ')
                + (applicant.zip_code || applicant.city ? ', ' : '')
                + [applicant.zip_code, applicant.city].filter(Boolean).join(' ')
                || undefined
              } />
            </div>
          )}
        </div>

        {/* Beschäftigungswünsche */}
        <div style={{ ...ps.card, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: colors.navy }}>Beschäftigungswünsche</h3>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <EditField label="Team" icon={<FaBriefcase />}>
                <select value={form.team_choice || ''} onChange={e => updateForm({ team_choice: e.target.value as any || undefined })} style={selectStyle}>
                  <option value="">–</option>
                  <option value="SERVICE">Service</option>
                  <option value="PRODUCTION">Produktion</option>
                </select>
              </EditField>
              <EditField label="Beschäftigungsart" icon={<FaBriefcase />}>
                <select value={form.desired_employment_type || ''} onChange={e => updateForm({ desired_employment_type: e.target.value as any || undefined })} style={selectStyle}>
                  <option value="">–</option>
                  <option value="MINIJOB">Minijob</option>
                  <option value="WERKSTUDENT">Werkstudent</option>
                  <option value="TEILZEIT">Teilzeit</option>
                  <option value="VOLLZEIT">Vollzeit</option>
                  <option value="KURZFRISTIG">Kurzfristig</option>
                </select>
              </EditField>
              <EditField label="Gewünschter Start" icon={<FaCalendarAlt />}>
                <input type="date" value={form.desired_start_date || ''} onChange={e => updateForm({ desired_start_date: e.target.value })} style={inputStyle} />
              </EditField>
              <EditField label="Arbeitsbereich" icon={<FaBriefcase />}>
                <input value={form.desired_work_area || ''} onChange={e => updateForm({ desired_work_area: e.target.value })} style={inputStyle} />
              </EditField>
              <EditField label="Stunden/Woche" icon={<FaBriefcase />}>
                <input type="number" value={form.hours_can_work ?? ''} onChange={e => updateForm({ hours_can_work: e.target.value ? Number(e.target.value) : undefined })} style={inputStyle} />
              </EditField>
              <EditField label="Geburtsdatum" icon={<FaCalendarAlt />}>
                <input type="date" value={form.birthday || ''} onChange={e => updateForm({ birthday: e.target.value })} style={inputStyle} />
              </EditField>
              <EditField label="Über 18" icon={<FaIdCard />}>
                <select value={form.is_over_18 == null ? '' : form.is_over_18 ? 'true' : 'false'} onChange={e => updateForm({ is_over_18: e.target.value === '' ? undefined : e.target.value === 'true' })} style={selectStyle}>
                  <option value="">–</option>
                  <option value="true">Ja</option>
                  <option value="false">Nein</option>
                </select>
              </EditField>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <InfoRow icon={<FaBriefcase />} label="Team" value={applicant.team_choice === 'SERVICE' ? 'Service' : applicant.team_choice === 'PRODUCTION' ? 'Produktion' : undefined} />
              <InfoRow icon={<FaBriefcase />} label="Beschäftigungsart" value={applicant.desired_employment_type ? formatEmploymentType(applicant.desired_employment_type) : undefined} />
              <InfoRow icon={<FaCalendarAlt />} label="Gewünschter Start" value={applicant.desired_start_date ? formatDate(applicant.desired_start_date) : undefined} />
              <InfoRow icon={<FaBriefcase />} label="Arbeitsbereich" value={applicant.desired_work_area} />
              <InfoRow icon={<FaBriefcase />} label="Stunden/Woche" value={applicant.hours_can_work != null ? `${applicant.hours_can_work} h` : undefined} />
              <InfoRow icon={<FaCalendarAlt />} label="Geburtsdatum" value={applicant.birthday ? formatDate(applicant.birthday) : undefined} />
              <InfoRow icon={<FaIdCard />} label="Über 18" value={applicant.is_over_18 != null ? (applicant.is_over_18 ? 'Ja' : 'Nein') : undefined} />
            </div>
          )}
        </div>

        {/* Qualifikationen */}
        <div style={{ ...ps.card, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: colors.navy }}>Qualifikationen</h3>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <EditField label="Muttersprache" icon={<FaLanguage />}>
                <input value={form.native_language || ''} onChange={e => updateForm({ native_language: e.target.value })} style={inputStyle} />
              </EditField>
              <EditField label="Deutsch" icon={<FaLanguage />}>
                <select value={form.german_level || ''} onChange={e => updateForm({ german_level: e.target.value })} style={selectStyle}>
                  <option value="">–</option>
                  {GERMAN_LEVELS.map(l => <option key={l} value={l}>{GERMAN_LEVEL_LABELS[l] || l}</option>)}
                </select>
              </EditField>
              <EditField label="Englisch" icon={<FaLanguage />}>
                <input value={form.english_level || ''} onChange={e => updateForm({ english_level: e.target.value })} placeholder="z.B. B2, Gut, Fließend" style={inputStyle} />
              </EditField>
              <EditField label="Gastro-Erfahrung" icon={<FaUtensils />}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select value={form.has_gastro_experience == null ? '' : form.has_gastro_experience ? 'true' : 'false'}
                    onChange={e => updateForm({ has_gastro_experience: e.target.value === '' ? undefined : e.target.value === 'true' })} style={{ ...selectStyle, flex: 1 }}>
                    <option value="">–</option>
                    <option value="true">Ja</option>
                    <option value="false">Nein</option>
                  </select>
                  {form.has_gastro_experience && (
                    <input type="number" value={form.gastro_experience_years ?? ''} onChange={e => updateForm({ gastro_experience_years: e.target.value ? Number(e.target.value) : undefined })}
                      placeholder="Jahre" style={{ ...inputStyle, flex: 1 }} min={0} />
                  )}
                </div>
              </EditField>
              <EditField label="Rote Karte" icon={<FaIdCard />}>
                <select value={form.has_red_card == null ? '' : form.has_red_card ? 'true' : 'false'}
                  onChange={e => updateForm({ has_red_card: e.target.value === '' ? undefined : e.target.value === 'true' })} style={selectStyle}>
                  <option value="">–</option>
                  <option value="true">Ja</option>
                  <option value="false">Nein</option>
                </select>
              </EditField>
              <EditField label="Bereits beschäftigt" icon={<FaBriefcase />}>
                <select value={form.is_already_employed == null ? '' : form.is_already_employed ? 'true' : 'false'}
                  onChange={e => updateForm({ is_already_employed: e.target.value === '' ? undefined : e.target.value === 'true' })} style={selectStyle}>
                  <option value="">–</option>
                  <option value="true">Ja</option>
                  <option value="false">Nein</option>
                </select>
              </EditField>
              <EditField label="Aktueller Status" icon={<FaBriefcase />}>
                <input value={form.current_status || ''} onChange={e => updateForm({ current_status: e.target.value })} placeholder="z.B. Student, Arbeitsuchend" style={inputStyle} />
              </EditField>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <InfoRow icon={<FaLanguage />} label="Muttersprache" value={applicant.native_language} />
              <InfoRow icon={<FaLanguage />} label="Deutsch" value={applicant.german_level ? (GERMAN_LEVEL_LABELS[applicant.german_level] || applicant.german_level) : undefined} />
              <InfoRow icon={<FaLanguage />} label="Englisch" value={applicant.english_level} />
              <InfoRow icon={<FaUtensils />} label="Gastro-Erfahrung" value={applicant.has_gastro_experience != null ? (applicant.has_gastro_experience ? `Ja (${applicant.gastro_experience_years || 0} Jahre)` : 'Nein') : undefined} />
              <InfoRow icon={<FaIdCard />} label="Rote Karte" value={applicant.has_red_card != null ? (applicant.has_red_card ? 'Ja' : 'Nein') : undefined} />
              <InfoRow icon={<FaBriefcase />} label="Bereits beschäftigt" value={applicant.is_already_employed != null ? (applicant.is_already_employed ? 'Ja' : 'Nein') : undefined} />
              <InfoRow icon={<FaBriefcase />} label="Aktueller Status" value={applicant.current_status} />
              <InfoRow icon={<FaIdCard />} label="Führerschein" value={applicant.has_drivers_license != null ? (applicant.has_drivers_license ? 'Ja' : 'Nein') : undefined} />
              <InfoRow icon={<FaBriefcase />} label="Studiengang" value={applicant.study_program} />
              <InfoRow icon={<FaBriefcase />} label="Semester" value={applicant.semester} />
              <InfoRow icon={<FaIdCard />} label="Bachelor" value={applicant.has_bachelor != null ? (applicant.has_bachelor ? 'Ja' : 'Nein') : undefined} />
            </div>
          )}
        </div>

        {/* Persönliches */}
        <div style={{ ...ps.card, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: colors.navy }}>Persönliches</h3>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <EditField label="Lieblingseis" icon={<FaIceCream />}>
                <input value={form.favorite_ice_cream || ''} onChange={e => updateForm({ favorite_ice_cream: e.target.value })} style={inputStyle} />
              </EditField>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <InfoRow icon={<FaIceCream />} label="Lieblingseis" value={applicant.favorite_ice_cream} />
              {!applicant.favorite_ice_cream && (
                <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>Keine Angaben</div>
              )}
            </div>
          )}
        </div>

        {/* Anschreiben & Motivation (full width) */}
        <div style={{ ...ps.card, padding: 24, gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: colors.navy }}>Anschreiben & Motivation</h3>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <EditField label="Anschreiben" icon={<FaEdit />}>
                <textarea value={form.cover_letter || ''} onChange={e => updateForm({ cover_letter: e.target.value })}
                  rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
              </EditField>
              <EditField label="Motivation" icon={<FaEdit />}>
                <textarea value={form.motivation_text || ''} onChange={e => updateForm({ motivation_text: e.target.value })}
                  rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
              </EditField>
            </div>
          ) : (
            <div>
              {applicant.cover_letter && (
                <div style={{ fontSize: 13, color: colors.textBody, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{applicant.cover_letter}</div>
              )}
              {applicant.motivation_text && (
                <div style={{ fontSize: 13, color: colors.textBody, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: applicant.cover_letter ? 12 : 0 }}>{applicant.motivation_text}</div>
              )}
              {!applicant.cover_letter && !applicant.motivation_text && (
                <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>Kein Anschreiben oder Motivationstext vorhanden</div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Tab: Interviews */}
      {activeTab === 'interviews' && (
        <div style={{ ...ps.card, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.navy }}>
              Telefoninterviews ({interviews.length})
            </h3>
            {applicant.status !== 'HIRED' && applicant.status !== 'REJECTED' && (
              <button onClick={() => setShowInterviewForm(true)} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${colors.border}`, background: colors.bgCard,
                cursor: 'pointer', fontSize: 11, fontWeight: 500, color: colors.textBody,
              }}>
                <FaPhoneAlt style={{ fontSize: 9 }} /> Neues Interview
              </button>
            )}
          </div>

          {loadingInterviews ? (
            <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 12 }}>Lade Interviews...</div>
          ) : interviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 12, fontStyle: 'italic' }}>
              Noch keine Interviews durchgeführt.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {interviews.map(iv => (
                <InterviewCard key={iv.id} interview={iv} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Notizen */}
      {activeTab === 'notizen' && (
        <div style={{ ...ps.card, padding: 24 }}>
          <InternalNotesPanel applicantId={applicant.id} />
        </div>
      )}
      </div>

      {/* Phone Interview Modal */}
      {showInterviewForm && applicant && (
        <PhoneInterviewForm
          applicant={applicant}
          onClose={() => setShowInterviewForm(false)}
          onSaved={() => { setShowInterviewForm(false); loadApplicant(); loadInterviews(); }}
        />
      )}

      {/* Convert Confirmation Modal */}
      {showConvertConfirm && applicant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(14, 19, 51, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={e => { if (e.target === e.currentTarget) setShowConvertConfirm(false); }}>
          <div style={{ background: colors.bgCard, borderRadius: 16, padding: 28, width: 440, boxShadow: shadows.elevated }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
              <FaUserCheck style={{ marginRight: 8, color: colors.success }} />
              Als Mitarbeiter anlegen?
            </h3>
            <p style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5, margin: '0 0 16px' }}>
              Folgende Daten werden in den neuen Mitarbeiter-Datensatz übernommen:
            </p>
            <div style={{ background: colors.bgMuted, borderRadius: 10, padding: 14, fontSize: 12, color: colors.textBody, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                <span><strong>Name:</strong> {applicant.first_name} {applicant.last_name}</span>
                <span><strong>E-Mail:</strong> {applicant.email}</span>
                {applicant.phone && <span><strong>Telefon:</strong> {applicant.phone}</span>}
                {applicant.desired_employment_type && <span><strong>Typ:</strong> {formatEmploymentType(applicant.desired_employment_type)}</span>}
                {applicant.desired_start_date && <span><strong>Start:</strong> {formatDate(applicant.desired_start_date)}</span>}
                {(applicant.street || applicant.city) && (
                  <span><strong>Adresse:</strong> {[applicant.street, applicant.house_no, applicant.zip_code, applicant.city].filter(Boolean).join(', ')}</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowConvertConfirm(false)} style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
                background: colors.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 500,
              }}>Abbrechen</button>
              <button onClick={handleConvert} disabled={converting} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white',
                cursor: converting ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                opacity: converting ? 0.7 : 1,
              }}>{converting ? 'Wird angelegt...' : 'Ja, Mitarbeiter anlegen'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Helpers ---- */

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }): React.JSX.Element | null {
  if (!value || value === '–' || value === ', ') return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
      <span style={{ color: colors.textLight, fontSize: 12, width: 16, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 13, color: colors.textMuted, minWidth: 130 }}>
        <strong style={{ color: colors.textBody }}>{label}:</strong>
      </span>
      <span style={{ color: colors.textBody }}>{value}</span>
    </div>
  );
}

const RECOMMENDATION_LABELS: Record<InterviewRecommendation, { label: string; color: string }> = {
  HIRE: { label: 'Einstellen', color: '#22c55e' },
  TRIAL: { label: 'Probearbeiten', color: '#f59e0b' },
  REJECT: { label: 'Ablehnen', color: '#ef4444' },
  UNDECIDED: { label: 'Unentschieden', color: '#94a3b8' },
};

function InterviewCard({ interview }: { interview: PhoneInterview }): React.JSX.Element {
  const rec = RECOMMENDATION_LABELS[interview.recommendation] || RECOMMENDATION_LABELS.UNDECIDED;
  return (
    <div style={{
      padding: 16, borderRadius: 12, border: `1px solid ${colors.border}`,
      background: colors.bgCard,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FaPhoneAlt style={{ color: colors.slate, fontSize: 12 }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: colors.textBody }}>
            {new Date(interview.conducted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            {', '}
            {new Date(interview.conducted_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </span>
          {interview.interviewer_name && (
            <span style={{ fontSize: 12, color: colors.textMuted }}>
              von {interview.interviewer_name}
            </span>
          )}
        </div>
        <span style={{
          padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
          background: rec.color, color: 'white',
        }}>
          {rec.label}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 10, fontSize: 12 }}>
        <div style={{ display: 'flex', gap: 2 }}>
          {([1, 2, 3, 4, 5] as const).map(s => (
            s <= interview.overall_rating
              ? <FaStar key={s} color="#f59e0b" style={{ fontSize: 14 }} />
              : <FaRegStar key={s} color={colors.border} style={{ fontSize: 14 }} />
          ))}
        </div>
        <span style={{ color: colors.textMuted }}>
          Mindestkriterien: <strong style={{ color: interview.meets_minimum_criteria ? '#166534' : '#991b1b' }}>
            {interview.meets_minimum_criteria ? 'Ja' : 'Nein'}
          </strong>
        </span>
      </div>

      {/* Responses */}
      {Object.keys(interview.standard_responses || {}).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Standardfragen</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
            {Object.entries(interview.standard_responses).map(([key, val]) => (
              <div key={key} style={{ display: 'flex', gap: 6 }}>
                <span style={{ color: colors.textMuted }}>{key.replace(/_/g, ' ')}:</span>
                <span style={{ color: colors.textBody, fontWeight: 500 }}>{String(val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(interview.criteria_responses || {}).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Kriterien</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
            {Object.entries(interview.criteria_responses).map(([key, val]) => (
              <div key={key} style={{ display: 'flex', gap: 6 }}>
                <span style={{ color: colors.textMuted }}>{key}:</span>
                <span style={{ color: colors.textBody, fontWeight: 500 }}>{String(val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {interview.notes && (
        <div style={{
          marginTop: 8, padding: 10, borderRadius: 8, background: colors.bgMuted,
          fontSize: 12, color: colors.textBody, lineHeight: 1.5, whiteSpace: 'pre-wrap',
        }}>
          {interview.notes}
        </div>
      )}
    </div>
  );
}

function EditField({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13 }}>
      <span style={{ color: colors.textLight, fontSize: 12, width: 16, textAlign: 'center', flexShrink: 0, marginTop: 8 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>{label}</div>
        {children}
      </div>
    </div>
  );
}
